import { AutoComplete, Input } from 'antd'
import { useEffect, useState } from 'react'
import { useGooglePlacesAutocomplete } from '../../hooks/useGooglePlacesAutocomplete'

interface GooglePlacesAutocompleteProps {
  value?: string
  onChange?: (value: string) => void
  onPlaceSelected?: (parsedAddress: {
    address?: string
    city?: string
    postalCode?: string
    province?: string
    country?: string
    latitude?: number
    longitude?: number
  }) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  country?: string // 2-letter country code (e.g., 'CA', 'US')
}

/**
 * Google Places Autocomplete Component
 * Provides address autocomplete suggestions using Google Places API
 */
export const GooglePlacesAutocomplete: React.FC<GooglePlacesAutocompleteProps> = ({
  value,
  onChange,
  onPlaceSelected,
  placeholder,
  disabled,
  className,
  country,
}) => {
  const [inputValue, setInputValue] = useState(value || '')
  const [open, setOpen] = useState(false)

  const { suggestions, loading, onSearch, fetchPlaceDetails } = useGooglePlacesAutocomplete({
    onPlaceSelected: (parsedAddress) => {
      // Only call onPlaceSelected when a place is actually selected
      // This ensures city/postal code are only updated on selection, not typing
      if (onPlaceSelected) {
        onPlaceSelected(parsedAddress)
      }
      setOpen(false)
    },
    country,
  })

  useEffect(() => {
    setInputValue(value || '')
  }, [value])

  // Update open state when suggestions or loading state changes
  useEffect(() => {
    if (inputValue.trim().length > 0 && (suggestions.length > 0 || loading)) {
      setOpen(true)
    }
  }, [suggestions, loading, inputValue])

  const handleSearch = (searchText: string) => {
    setInputValue(searchText)
    if (onChange) {
      onChange(searchText)
    }

    if (searchText.trim().length > 0) {
      setOpen(true)
      onSearch(searchText)
    } else {
      setOpen(false)
    }
  }

  const handleSelect = async (selectedValue: string) => {
    // Find the selected suggestion
    const selectedSuggestion = suggestions.find(
      (s) => s?.placePrediction?.text?.text === selectedValue || s?.placePrediction?.structuredFormat?.mainText?.text === selectedValue
    )

    if (selectedSuggestion?.placePrediction) {
      const prediction = selectedSuggestion.placePrediction
      const placeId = prediction.placeId

      if (!placeId) {
        console.error('Place ID not found in suggestion')
        return
      }

      // Fetch place details to get the parsed address (street address only, no city/postal code)
      // This will call onPlaceSelected which will update address, city, and postal code
      const parsedAddress = await fetchPlaceDetails(placeId)
      
      // Use the parsed address field which contains only street number + route
      // This ensures we only show the street address, not city/postal code/province/country
      if (parsedAddress?.address) {
        setInputValue(parsedAddress.address)
        if (onChange) {
          onChange(parsedAddress.address)
        }
      } else {
        // Fallback: use mainText from structured format (should be just street address)
        const mainText = prediction.structuredFormat?.mainText?.text || prediction.text?.text || selectedValue
        // Extract just the street address part (before any comma) as fallback
        const streetAddress = mainText.split(',')[0].trim()
        setInputValue(streetAddress)
        if (onChange) {
          onChange(streetAddress)
        }
      }
    }
  }

  const handleFocus = () => {
    if (inputValue.trim().length > 0 && (suggestions.length > 0 || loading)) {
      setOpen(true)
    }
  }

  const handleBlur = () => {
    // Delay closing to allow click events to fire
    setTimeout(() => {
      setOpen(false)
    }, 200)
  }

  // Transform suggestions into AutoComplete options
  const options = suggestions
    .filter((suggestion) => suggestion?.placePrediction) // Filter out invalid suggestions
    .map((suggestion) => {
      const prediction = suggestion.placePrediction
      const mainText = prediction?.structuredFormat?.mainText?.text || prediction?.text?.text || ''
      const secondaryText = prediction?.structuredFormat?.secondaryText?.text || ''
      const fullText = prediction?.text?.text || mainText

      return {
        value: fullText,
        label: (
          <div>
            <div style={{ fontWeight: 500 }}>{mainText}</div>
            {secondaryText && <div style={{ fontSize: '12px', color: '#999' }}>{secondaryText}</div>}
          </div>
        ),
      }
    })

  return (
    <AutoComplete
      className={className}
      value={inputValue}
      options={options}
      onSearch={handleSearch}
      onSelect={handleSelect}
      onFocus={handleFocus}
      onBlur={handleBlur}
      open={open}
      disabled={disabled}
      notFoundContent={
        loading
          ? 'Searching...'
          : suggestions.length === 0 && inputValue.trim().length > 0
          ? 'No addresses found'
          : null
      }
      filterOption={false}
      allowClear
      dropdownMatchSelectWidth={true}
    >
      <Input placeholder={placeholder} onFocus={handleFocus} />
    </AutoComplete>
  )
}

